import React from 'react'
import ReactECharts from 'echarts-for-react'
import { EChartsOption } from 'echarts'
import { useTheme } from '../../../components/ThemeProvider'
import { getThemeSecondaryColor } from '../../../utils/themeConfig'

interface SummarizedLoansChartProps {
  data: any
}

export default function SummarizedLoansChart({ data }: SummarizedLoansChartProps) {
  const theme = useTheme()

  const periodKeys = [
    {
      label: '近1天',
      keys: [
        'loan_info_xyp_cpl0070',
        'loan_info_xyp_cpl0017',
        'loan_info_xyp_cpl0033',
        'loan_info_xyp_cpl0016',
        'loan_info_xyp_cpl0032',
      ],
    },
    {
      label: '近7天',
      keys: [
        'loan_info_xyp_cpl0009',
        'loan_info_xyp_cpl0019',
        'loan_info_xyp_cpl0035',
        'loan_info_xyp_cpl0018',
        'loan_info_xyp_cpl0034',
      ],
    },
    {
      label: '近14天',
      keys: [
        'loan_info_xyp_cpl0010',
        'loan_info_xyp_cpl0021',
        'loan_info_xyp_cpl0037',
        'loan_info_xyp_cpl0020',
        'loan_info_xyp_cpl0036',
      ],
    },
    {
      label: '近21天',
      keys: [
        'loan_info_xyp_cpl0063',
        'loan_info_xyp_cpl0064',
        'loan_info_xyp_cpl0067',
        'loan_info_xyp_cpl0065',
        'loan_info_xyp_cpl0066',
      ],
    },
    {
      label: '近30天',
      keys: [
        'loan_info_xyp_cpl0011',
        'loan_info_xyp_cpl0023',
        'loan_info_xyp_cpl0039',
        'loan_info_xyp_cpl0022',
        'loan_info_xyp_cpl0038',
      ],
    },
  ]

  const metricLabels = ['贷款机构数', '还款成功笔数', '还款成功金额', '交易失败笔数', '交易失败金额']
  const metricColors = [
    theme?.primaryColor || '#3B82F6',
    getThemeSecondaryColor(theme?.type),
    '#10B981',
    '#F59E0B',
    '#EF4444',
  ]

  const getNumericValue = (value: any): number => {
    if (value === null || value === undefined || value === '' || value === '—') return 0
    const numValue = Number(value)
    return isNaN(numValue) ? 0 : numValue
  }

  const options: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
    },
    legend: {
      data: metricLabels,
      bottom: 0,
      textStyle: {
        fontSize: 11,
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%',
      top: '5%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: periodKeys.map((p) => p.label),
      axisLabel: {
        fontSize: 10,
      },
    },
    yAxis: {
      type: 'value',
      min: 0,
      name: '等级',
      nameTextStyle: {
        fontSize: 11,
      },
      axisLabel: {
        formatter: '{value}',
      },
    },
    series: [
      {
        name: '贷款机构数',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: metricColors[0],
        },
        data: [
          getNumericValue(data?.loan_info_xyp_cpl0070),
          getNumericValue(data?.loan_info_xyp_cpl0009),
          getNumericValue(data?.loan_info_xyp_cpl0010),
          getNumericValue(data?.loan_info_xyp_cpl0063),
          getNumericValue(data?.loan_info_xyp_cpl0011),
        ],
      },
      {
        name: '还款成功笔数',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: metricColors[1],
        },
        data: [
          getNumericValue(data?.loan_info_xyp_cpl0017),
          getNumericValue(data?.loan_info_xyp_cpl0019),
          getNumericValue(data?.loan_info_xyp_cpl0021),
          getNumericValue(data?.loan_info_xyp_cpl0064),
          getNumericValue(data?.loan_info_xyp_cpl0023),
        ],
      },
      {
        name: '还款成功金额',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: metricColors[2],
        },
        data: [
          getNumericValue(data?.loan_info_xyp_cpl0033),
          getNumericValue(data?.loan_info_xyp_cpl0035),
          getNumericValue(data?.loan_info_xyp_cpl0037),
          getNumericValue(data?.loan_info_xyp_cpl0067),
          getNumericValue(data?.loan_info_xyp_cpl0039),
        ],
      },
      {
        name: '交易失败笔数',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: metricColors[3],
        },
        data: [
          getNumericValue(data?.loan_info_xyp_cpl0016),
          getNumericValue(data?.loan_info_xyp_cpl0018),
          getNumericValue(data?.loan_info_xyp_cpl0020),
          getNumericValue(data?.loan_info_xyp_cpl0065),
          getNumericValue(data?.loan_info_xyp_cpl0022),
        ],
      },
      {
        name: '交易失败金额',
        type: 'bar',
        label: {
          show: true,
          color: '#ffffff',
          fontSize: 10,
        },
        itemStyle: {
          color: metricColors[4],
        },
        data: [
          getNumericValue(data?.loan_info_xyp_cpl0032),
          getNumericValue(data?.loan_info_xyp_cpl0034),
          getNumericValue(data?.loan_info_xyp_cpl0036),
          getNumericValue(data?.loan_info_xyp_cpl0066),
          getNumericValue(data?.loan_info_xyp_cpl0038),
        ],
      },
    ],
  }

  return <ReactECharts option={options} style={{ height: '400px' }} />
}
